import { PdfTextLine } from './pdfTextLines';
import { ColumnBand, readLineCells } from './lvColumnLayout';
import { isTenderPositionNumber, isTenderSumLine } from '../tenderLvImport';

/**
 * Der Notausgang des LV-Imports: der Nutzer markiert die Positionen selbst im
 * Original-PDF, statt die KI suchen zu lassen.
 *
 * Warum das der bessere Weg ist, wenn die Extraktion daneben liegt: das FINDEN
 * der Positionen ist die Stelle, an der alles schief geht — verschluckte
 * Zeilen, erfundene Zeilen, Summenzeilen als Titel, gebrochene Reihenfolge.
 * Steht die Auswahl fest, ist der Rest Geometrie. Die KI wird von der Sucherin
 * zur Aufraeumerin und bekommt nur noch die markierten Bloecke vorgelegt.
 *
 * Der Modus liefert am Ende exakt dieselbe Zeilenform wie `extractTenderLV`.
 * Damit laeuft alles Nachgelagerte unveraendert weiter: Einstufung,
 * Abschnitts-Vererbung, Duplikatpruefung, das Review und die Uebernahme ins
 * Angebot.
 *
 * Rein und ohne pdfjs/DOM — die gesamte Ableitung ist ohne Worker testbar.
 */

export type LineMark = 'position' | 'heading';

export interface LvMarkingState {
    /** Spaltenraster, gilt fuer das ganze Dokument. */
    bands: ColumnBand[];
    /** Zeilen-Id -> was der Nutzer daraus machen will. */
    marks: Record<string, LineMark>;
}

export const emptyMarkingState = (bands: ColumnBand[] = []): LvMarkingState => ({ bands, marks: {} });

// ─── Zustandsuebergaenge (alle pur) ──────────────────────────────────────────

export const setBandRole = (state: LvMarkingState, index: number, role: ColumnBand['role']): LvMarkingState => ({
    ...state,
    bands: state.bands.map((b, i) => (i === index ? { ...b, role } : b)),
});

export const setMarks = (state: LvMarkingState, ids: string[], mark: LineMark | null): LvMarkingState => {
    if (!ids.length) return state;
    const marks = { ...state.marks };
    for (const id of ids) {
        if (mark === null) delete marks[id];
        else marks[id] = mark;
    }
    return { ...state, marks };
};

export const toggleMark = (state: LvMarkingState, id: string, mark: LineMark): LvMarkingState =>
    setMarks(state, [id], state.marks[id] === mark ? null : mark);

/** Alle Markierungen einer Seite loeschen; ohne Seitenangabe das ganze Dokument. */
export const clearMarks = (state: LvMarkingState, pageIndex?: number): LvMarkingState => {
    if (pageIndex === undefined) return { ...state, marks: {} };
    const prefix = `${pageIndex}:`;
    const marks: Record<string, LineMark> = {};
    for (const [id, m] of Object.entries(state.marks)) {
        if (!id.startsWith(prefix)) marks[id] = m;
    }
    return { ...state, marks };
};

/**
 * Vorauswahl ueber das Spaltenraster: jede Zeile, die in der Nummernspalte
 * eine Ordnungszahl traegt, ist eine Position — und die Zeilen darunter
 * gehoeren als Fortsetzungstext dazu, bis die naechste Nummer kommt.
 *
 * Das ist der Grundmodus fuer grosse LVs: vier Klicks fuer die Spaltenrollen,
 * danach steht die Auswahl fuer alle Seiten und der Nutzer korrigiert nur die
 * Ausreisser. Einzeln anzuklicken waeren es bei 120 Positionen ueber 400 Zeilen.
 */
export const autoMarkPositions = (state: LvMarkingState, lines: PdfTextLine[]): LvMarkingState => {
    const marks: Record<string, LineMark> = {};
    const dominant = dominantLineSize(lines);
    const running = runningHeaderLineIds(lines);
    const ozByLine = new Map<string, boolean>();
    for (const l of lines) ozByLine.set(l.id, isTenderPositionNumber(readLineCells(l, state.bands).position));
    const anyOz = [...ozByLine.values()].some(Boolean);
    // Gibt es auf DIESER Seite groesser gesetzte Zeilen? In LVs, die alles in
    // einer Groesse setzen, wuerde die Groessen-Heuristik nie einen Titel
    // finden und saemtliche Abschnitte blieben leer.
    //
    // Bewusst je Seite und ohne die Seitenkoepfe: dokumentweit gerechnet
    // genuegte EINE grosse Kopfzeile auf Seite 1, um den Ersatzweg fuer das
    // ganze Dokument abzuschalten — danach wurde kein Titel mehr erkannt.
    const sizeSeparatesOnPage = new Map<number, boolean>();
    for (const l of lines) {
        if (running.has(l.id)) continue;
        if (l.size >= dominant + 0.5) sizeSeparatesOnPage.set(l.pageIndex, true);
    }

    let openPosition = false;
    let lastY: number | null = null;
    let lastPage = -1;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        const cells = readLineCells(line, state.bands);
        const hasOz = ozByLine.get(line.id) === true;

        // Seitenkopf/-fuss: gleicher Text an gleicher Hoehe auf mehreren
        // Seiten. Ohne diese Ausnahme wurde ein 11-pt-Bauvorhabenkopf auf
        // JEDER Seite zum Titel, und alle folgenden Positionen erbten "Neubau
        // Musterstraße 12" statt ihres echten Abschnitts. Eine Zeile mit
        // eigener Ordnungszahl ist nie ein Seitenkopf — dieser Schutz gilt der
        // Gegenrichtung: eine echte Position darf nie verschluckt werden.
        if (!hasOz && running.has(line.id)) continue;
        const sum = isTenderSumLine(line.text);

        if (hasOz && !sum) {
            marks[line.id] = 'position';
            openPosition = true;
            lastY = line.y;
            lastPage = line.pageIndex;
            continue;
        }

        if (sum) {
            openPosition = false;
            continue;
        }

        // Ueberschrift. Erste Wahl: groesser gesetzt als der Fliesstext.
        // Setzt das Dokument alles gleich gross, gilt ersatzweise: kurze Zeile
        // ohne Nummer, direkt vor einer Zeile MIT Nummer.
        const short = line.text.length <= 90;
        const isHeading = short && !hasOz && (
            sizeSeparatesOnPage.get(line.pageIndex)
                ? line.size >= dominant + 0.5
                : (anyOz && ozByLine.get(nextContentLineId(lines, i) ?? '') === true)
        );
        if (isHeading) {
            marks[line.id] = 'heading';
            openPosition = false;
            continue;
        }

        if (!openPosition) continue;

        // Fortsetzungstext derselben Position: gleiche Seite, kein grosser
        // vertikaler Sprung (sonst waere es die Fusszeile) und Text in der
        // Bezeichnungsspalte.
        const samePage = line.pageIndex === lastPage;
        const gap = lastY === null ? 0 : lastY - line.y;
        if (samePage && gap >= 0 && gap <= line.size * 2.5 && cells.bezeichnung) {
            marks[line.id] = 'position';
            lastY = line.y;
        } else {
            openPosition = false;
        }
    }

    return { ...state, marks };
};

const nextContentLineId = (lines: PdfTextLine[], from: number): string | null =>
    from + 1 < lines.length ? lines[from + 1].id : null;

const normalizeForRepeat = (s: string): string =>
    s.toLowerCase().replace(/\d+/g, '#').replace(/\s+/g, ' ').trim();

/** Hoehen-Toleranz, innerhalb derer zwei Zeilen als "gleiche Stelle" gelten. */
const REPEAT_Y_TOL = 2.5;

/**
 * Zeilen, die auf mindestens zwei Seiten mit gleichem Text an gleicher Stelle
 * wiederkehren — Seitenkopf, Fusszeile, Spaltenkopf. Geliefert werden die
 * Zeilen-IDs.
 *
 * Die Hoehe MUSS in die Pruefung. Ein Seitenkopf steht auf jeder Seite an
 * derselben Stelle; eine echte Leistung wie "Baustellenbegehung" kommt in
 * einem LV ebenfalls auf mehreren Seiten vor, aber an wechselnder Hoehe. Ueber
 * den Text allein verschluckte die Pruefung genau die Positionen, um die es
 * geht — bei einem LV, dessen Zeilen sich nur in der Nummer unterscheiden,
 * markierte die Automatik gar nichts mehr.
 *
 * Bewusst mit einer Toleranz und NICHT ueber gerundete Klassen: bei leicht
 * unterschiedlichen Kopfhoehen (1-2 pt) faellt eine Klassengrenze sonst
 * mitten durch denselben Kopf, und er wird zur Titelzeile.
 * Ziffern werden im Text maskiert, damit "Seite 3 von 12" als derselbe Kopf
 * gilt wie "Seite 4 von 12".
 */
export const runningHeaderLineIds = (lines: PdfTextLine[]): Set<string> => {
    const groupsByText = new Map<string, { y: number; pages: Set<number>; ids: string[] }[]>();
    for (const l of lines) {
        const text = normalizeForRepeat(l.text);
        if (!text) continue;
        const groups = groupsByText.get(text) ?? [];
        const hit = groups.find((g) => Math.abs(g.y - l.y) <= REPEAT_Y_TOL);
        if (hit) {
            hit.pages.add(l.pageIndex);
            hit.ids.push(l.id);
        } else {
            groups.push({ y: l.y, pages: new Set([l.pageIndex]), ids: [l.id] });
        }
        groupsByText.set(text, groups);
    }

    const out = new Set<string>();
    for (const groups of groupsByText.values()) {
        for (const g of groups) {
            if (g.pages.size >= 2) g.ids.forEach((id) => out.add(id));
        }
    }
    return out;
};

/**
 * Ist eine LV-Nummernspalte zugeordnet? Ohne sie fehlt der Zeilentrenner: dann
 * darf `rowsFromMarking` mehrzeilige Texte NICHT zusammenfassen, sonst wird aus
 * 120 markierten Positionen eine einzige Zeile mit 40.000 Zeichen.
 */
export const hasPositionColumn = (state: LvMarkingState): boolean =>
    state.bands.some((b) => b.role === 'pos');

/**
 * Trennt ueberhaupt irgendeine Spalte die Zeilen? Ohne `pos` uebernimmt
 * Menge/Einheit die Rolle; fehlt auch die, bleibt nur "jede Zeile fuer sich".
 */
export const hasRowSplitter = (state: LvMarkingState): boolean =>
    state.bands.some((b) => b.role === 'pos' || b.role === 'menge' || b.role === 'einheit');

const dominantLineSize = (lines: PdfTextLine[]): number => {
    const counts = new Map<number, number>();
    for (const l of lines) {
        const key = Math.round(l.size * 2) / 2;
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    if (!counts.size) return 9;
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
};

// ─── Ableitung der LV-Zeilen ─────────────────────────────────────────────────

/** Rohzeile in derselben Form, die `extractTenderLV` liefert. */
export interface MarkedLvRow {
    position: string;
    bezeichnung: string;
    einheit: string;
    menge: number;
    abschnitt: string;
    /** Zeilen-Ids, aus denen die Position entstanden ist (fuer die Rueckschau). */
    lineIds: string[];
}

/**
 * Markierte Zeilen -> LV-Rohzeilen, in Dokumentreihenfolge.
 *
 * Mehrzeilige Leistungstexte werden zusammengefuehrt: eine markierte Zeile
 * ohne eigene Ordnungszahl gehoert zur Position darueber. Genau daran ist die
 * KI-Extraktion regelmaessig gescheitert — der zweite Teil einer ueber den
 * Seitenumbruch geteilten Position kam als eigene, mengelose Zeile zurueck und
 * wurde dort zur Ueberschrift.
 */
export const rowsFromMarking = (lines: PdfTextLine[], state: LvMarkingState): MarkedLvRow[] => {
    const out: MarkedLvRow[] = [];
    let section = '';
    let open: MarkedLvRow | null = null;
    // Ohne Nummernspalte gibt es keinen verlaesslichen Zeilentrenner. Dann
    // trennt ersatzweise jede Zeile, die etwas Strukturiertes traegt (Menge
    // oder Einheit). Ist AUCH das nicht zugeordnet, gibt es gar keinen
    // Trenner — dann wird jede markierte Zeile eine eigene Position. Lieber zu
    // viele Zeilen, die der Nutzer im Review zusammenschiebt, als die eine
    // Riesenzeile, in der 120 Positionen verschwinden.
    const mergeContinuations = hasPositionColumn(state);
    const hasSplitter = state.bands.some((b) => b.role === 'menge' || b.role === 'einheit');

    for (const line of lines) {
        const mark = state.marks[line.id];
        if (!mark) continue;

        if (mark === 'heading') {
            section = line.text;
            open = null;
            // Die Ueberschrift selbst geht als eigene Zeile mit — der
            // nachgelagerte Import stuft sie als 'heading' ein und macht daraus
            // den Abschnitt. So bleibt es EINE Wahrheit.
            out.push({ position: '', bezeichnung: line.text, einheit: '', menge: 0, abschnitt: section, lineIds: [line.id] });
            continue;
        }

        const cells = readLineCells(line, state.bands);
        // Mit Nummernspalte trennt die Ordnungszahl. Ohne sie trennt jede
        // Zeile, die ueberhaupt etwas Strukturiertes traegt (Menge oder
        // Einheit) — eine reine Textzeile bleibt Fortsetzung. Sonst wuerde
        // jeder umgebrochene Leistungstext eine eigene Zeile ohne Menge und
        // Einheit, und die wird nachgelagert als Ueberschrift eingestuft: der
        // Fortsetzungstext geht verloren UND verbiegt den Abschnitt.
        const startsNew = open === null
            || (mergeContinuations
                ? isTenderPositionNumber(cells.position)
                : (hasSplitter ? (cells.menge > 0 || !!cells.einheit) : true));

        if (startsNew) {
            open = {
                position: cells.position,
                bezeichnung: cells.bezeichnung,
                einheit: cells.einheit,
                menge: cells.menge,
                abschnitt: section,
                lineIds: [line.id],
            };
            out.push(open);
            continue;
        }

        // Fortsetzung: Text anhaengen, leere Felder nachtragen.
        open.bezeichnung = [open.bezeichnung, cells.bezeichnung].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
        if (!open.einheit && cells.einheit) open.einheit = cells.einheit;
        if (!(open.menge > 0) && cells.menge > 0) open.menge = cells.menge;
        open.lineIds.push(line.id);
    }

    return out;
};

export interface MarkingSummary {
    markedLines: number;
    positions: number;
    headings: number;
    withoutQuantity: number;
    withoutNumber: number;
}

export const summarizeMarking = (lines: PdfTextLine[], state: LvMarkingState): MarkingSummary => {
    const rows = rowsFromMarking(lines, state);
    const headings = rows.filter((r) => !r.position && !r.einheit && !(r.menge > 0)
        && Object.values(state.marks).length > 0
        && r.lineIds.every((id) => state.marks[id] === 'heading')).length;
    const positions = rows.length - headings;
    return {
        markedLines: Object.keys(state.marks).length,
        positions,
        headings,
        withoutQuantity: rows.filter((r) => !(r.menge > 0) && !r.lineIds.every((id) => state.marks[id] === 'heading')).length,
        withoutNumber: rows.filter((r) => !r.position && !r.lineIds.every((id) => state.marks[id] === 'heading')).length,
    };
};
